import { apiRequest } from './axiosConfig';
import { handleApiError } from '../../utils/errorHandling';

// News article type definitions
export interface NewsArticle {
  id: number;
  title: string;
  content: string;
  thumbnail_url: string | null;
  /** トップに自動再生で出す動画（S3 の URL）。未設定なら従来どおりサムネイル画像だけ。 */
  hero_video_url: string | null;
  is_published: boolean;
  show_as_popup: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NewsPaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface NewsListResponse {
  data: NewsArticle[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface NewsCreateRequest {
  title: string;
  content: string;
  thumbnail_url?: string | null;
  hero_video_url?: string | null;
  is_published: boolean;
  show_as_popup?: boolean;
}

export interface NewsUpdateRequest {
  title?: string;
  content?: string;
  thumbnail_url?: string | null;
  hero_video_url?: string | null;
  is_published?: boolean;
  show_as_popup?: boolean;
}

// User-facing: fetch published news articles
export const fetchNews = async (page: number = 1): Promise<NewsListResponse> => {
  try {
    const response = await apiRequest.get<{ data: NewsListResponse }>(`/news?page=${page}`);
    return (response as any).data as NewsListResponse;
  } catch (error) {
    throw handleApiError(error, 'ニュース一覧の取得に失敗しました');
  }
};

// User-facing: fetch single published news article
export const fetchNewsById = async (id: number): Promise<NewsArticle> => {
  try {
    const response = await apiRequest.get<{ data: NewsArticle }>(`/news/${id}`);
    return (response as any).data as NewsArticle;
  } catch (error) {
    throw handleApiError(error, 'ニュース記事の取得に失敗しました');
  }
};

// User-facing: fetch popup news for the logged-in home (published + show_as_popup)
// ポップアップは付加的なUIなので、失敗時は静かにスキップして空配列を返す。
// サーバー側で「そのユーザーが非表示にしたニュース」は既に除外されている。
export const fetchPopupNews = async (limit: number = 5): Promise<NewsArticle[]> => {
  try {
    const response = await apiRequest.get<{ data: NewsArticle[] }>(`/news/popup?limit=${limit}`);
    return ((response as any).data as NewsArticle[]) ?? [];
  } catch (error) {
    return [];
  }
};

// User-facing: ユーザーごとにポップアップ通知を非表示にする（サーバー側に (user, news) で記録）。
// ベストエフォート（失敗してもUIは閉じる。次回ロードで再表示される可能性のみ）。
export const dismissPopupNews = async (newsIds: number[]): Promise<void> => {
  if (newsIds.length === 0) return;
  try {
    await apiRequest.post('/news/dismiss', { news_ids: newsIds });
  } catch (error) {
    // no-op
  }
};

// Admin: fetch all news articles (published and unpublished)
export const adminFetchNews = async (page: number = 1): Promise<NewsListResponse> => {
  try {
    const response = await apiRequest.get<{ data: NewsListResponse }>(`/admin/news?page=${page}`);
    return (response as any).data as NewsListResponse;
  } catch (error) {
    throw handleApiError(error, 'ニュース一覧の取得に失敗しました');
  }
};

// Admin: create news article
export const adminCreateNews = async (data: NewsCreateRequest): Promise<NewsArticle> => {
  try {
    const response = await apiRequest.post<{ data: NewsArticle }>('/admin/news', data);
    return (response as any).data as NewsArticle;
  } catch (error) {
    throw handleApiError(error, 'ニュース記事の作成に失敗しました');
  }
};

// Admin: update news article
export const adminUpdateNews = async (id: number, data: NewsUpdateRequest): Promise<NewsArticle> => {
  try {
    const response = await apiRequest.put<{ data: NewsArticle }>(`/admin/news/${id}`, data);
    return (response as any).data as NewsArticle;
  } catch (error) {
    throw handleApiError(error, 'ニュース記事の更新に失敗しました');
  }
};

// Admin: delete news article
export const adminDeleteNews = async (id: number): Promise<void> => {
  try {
    await apiRequest.delete(`/admin/news/${id}`);
  } catch (error) {
    throw handleApiError(error, 'ニュース記事の削除に失敗しました');
  }
};

// Admin: upload an image for a news article (thumbnail or inline body image)
// S3 にアップロードして表示用 URL を返す。
export const adminUploadNewsImage = async (file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    const response = await apiRequest.post<{ data: { url: string; key: string } }>(
      '/admin/news/upload-image',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return (response as any).data.url as string;
  } catch (error) {
    throw handleApiError(error, '画像のアップロードに失敗しました');
  }
};

// Admin: upload a hero video for a news article
// 画像アップロード（upload-image）と同じ応答形（{ data: { url, key } }）。
export const adminUploadNewsVideo = async (file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('video', file);
    const response = await apiRequest.post<{ data: { url: string; key: string } }>(
      '/admin/news/upload-video',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return (response as any).data.url as string;
  } catch (error) {
    throw handleApiError(error, '動画のアップロードに失敗しました');
  }
};
